// A name, normalised for matching — T43 (6 Sep 2026).
//
// WHY THIS EXISTS. An import names a client and a role per row, as typed into a spreadsheet, and
// the same firm arrives as `NORTHWIND CAPITAL`, `Northwind Capital Ltd.` and `The Northwind
// Capital Group` on three lists (Natanel, 6 Sep 2026: capitals or a slightly different spelling
// must still be the same client). Matching on the text as typed would create the client three
// times; matching by hand is the thing the import exists to remove. So a name is reduced to a
// KEY, and two names with one key are one thing.
//
// WHY IT LIVES HERE and not in the BFF. It is a pure function of a string, it has no database in
// it, and the brain's New client and New search forms use the same key to catch a typed
// duplicate (T43e, 11 Sep 2026; `apps/console/src/duplicates.ts`) — a browser can import this
// package's pure modules and cannot import the BFF. So it is exported on its own path,
// `@vera/db/match-key`, as `client-status` is: the package root pulls in Drizzle and the driver,
// and this file must keep importing nothing. One home, one set of tests.
//
// TWO TIERS, both automatic, one flagged. An EQUAL key is the same thing, silently. A CLOSE key —
// one edit apart, or one token more or fewer — is also matched, but the preview says so and
// offers "create as new instead": a typo and a genuinely different firm look alike at that
// distance, and a person should see which the import chose. Anything further apart is new.

/**
 * Trailing words that say what kind of company something is rather than which one. Dropped from
 * the END of a key, repeatedly, so `Northwind Capital Group Ltd` and `Northwind Capital` agree.
 * Never dropped when they are all that is left: `The Group` stays `group`.
 */
export const LEGAL_SUFFIXES: readonly string[] = [
  'ltd',
  'limited',
  'inc',
  'llc',
  'plc',
  'ab',
  'as',
  'oy',
  'gmbh',
  'sa',
  'bv',
  'co',
  'corp',
  'corporation',
  'company',
  'group',
  'holdings',
  'partners',
];

/** Letters NFD leaves whole. `Ö` decomposes to `O` + a mark; `ø` and `ß` do not. */
const UNDECOMPOSED: Readonly<Record<string, string>> = {
  ø: 'o',
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  ł: 'l',
  đ: 'd',
  þ: 'th',
  ð: 'd',
};

/**
 * The key's fold on its own (T202 K-26): lower-case, accents folded, `&` read as "and", every other
 * mark dropped and whitespace collapsed, with no "the" and no suffix taken off. A text is folded
 * this way to look a name's key up in it, so the name and the text agree on `&` and `ø`.
 */
export function foldText(text: string): string {
  return text
    .toLowerCase()
    .replace(/&/g, ' and ')
    .normalize('NFD')
    .replace(/\p{M}+/gu, '')
    .replace(/[øßæœłđþð]/g, (c) => UNDECOMPOSED[c] ?? c)
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * The key. Lower-case; accents folded (`Ö` → `o`); `&` read as "and"; every other punctuation
 * mark and symbol dropped; whitespace collapsed; a leading "the" dropped; trailing legal suffixes
 * dropped. `''` for a name with nothing in it.
 */
export function matchKey(name: string): string {
  let text = foldText(name);
  if (text.startsWith('the ')) text = text.slice(4);

  const tokens = text.split(' ').filter(Boolean);
  while (tokens.length > 1 && LEGAL_SUFFIXES.includes(tokens[tokens.length - 1]!)) {
    tokens.pop();
  }
  return tokens.join(' ');
}

/** How two keys relate. `equal` is the same thing; `close` is matched and flagged; `different` is new. */
export type KeyCloseness = 'equal' | 'close' | 'different';

/**
 * Keys shorter than this match exactly or not at all. One edit between `ibm` and `ib` is not a
 * typo, it is a different string, and the edit rule exists for names long enough to mistype.
 */
const MIN_FUZZY_LENGTH = 4;

/** Levenshtein, capped: the answer is only ever "is it 1", so anything past 2 is reported as 2. */
function editDistanceUpToTwo(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 1) return 2;
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(previous[j]! + 1, current[j - 1]! + 1, previous[j - 1]! + cost);
    }
    previous = current;
    if (Math.min(...previous) > 1) return 2;
  }
  return Math.min(previous[b.length]!, 2);
}

/** Is `shorter` `longer` with exactly one token removed, order kept? */
function oneTokenApart(shorter: string[], longer: string[]): boolean {
  if (longer.length !== shorter.length + 1) return false;
  let skipped = false;
  let i = 0;
  for (const token of longer) {
    if (shorter[i] === token) {
      i++;
    } else if (skipped) {
      return false;
    } else {
      skipped = true;
    }
  }
  return true;
}

export function keyCloseness(a: string, b: string): KeyCloseness {
  if (a === b) return 'equal';
  if (a === '' || b === '') return 'different';
  if (a.length >= MIN_FUZZY_LENGTH && b.length >= MIN_FUZZY_LENGTH) {
    if (editDistanceUpToTwo(a, b) === 1) return 'close';
  }
  const ta = a.split(' ');
  const tb = b.split(' ');
  if (ta.length > 1 || tb.length > 1) {
    if (oneTokenApart(ta, tb) || oneTokenApart(tb, ta)) return 'close';
  }
  return 'different';
}

export interface NameMatch<T> {
  closeness: Exclude<KeyCloseness, 'different'>;
  item: T;
}

/**
 * The existing thing a typed name refers to, if any. An equal key wins outright; failing that
 * the first close one, in the order given (a list ordered by name is stable across calls).
 */
export function findByName<T extends { name: string }>(
  name: string,
  among: readonly T[],
): NameMatch<T> | null {
  const key = matchKey(name);
  if (key === '') return null;
  let close: T | null = null;
  for (const item of among) {
    const closeness = keyCloseness(key, matchKey(item.name));
    if (closeness === 'equal') return { closeness, item };
    if (closeness === 'close' && close === null) close = item;
  }
  return close ? { closeness: 'close', item: close } : null;
}
